import { useState, useEffect } from 'react';
import { useDate } from '../../contexts/date-context';
import { Button } from '../ui/button';
import { FormField, Input, Label } from '../ui/input';
import { CurrencyInput } from '../ui/currency-input';
import type { ComponentProps } from 'react';
import type { Income } from '../../types';

export interface IncomeFormData {
  title: string;
  value: number;
  month: number;
  year: number;
}

export interface IncomeFormProps extends Omit<
  ComponentProps<'form'>,
  'onSubmit'
> {
  onSubmit: (data: IncomeFormData) => void;
  onClose?: () => void;
  initialData?: Income | null;
}

export function IncomeForm({
  onSubmit,
  onClose,
  initialData,
  ...props
}: IncomeFormProps) {
  const { month, year } = useDate();
  const [title, setTitle] = useState(initialData?.title || '');
  const [value, setValue] = useState(initialData?.value || 0);

  const isEditing = !!initialData;

  // Reset form when initialData changes
  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title);
      setValue(initialData.value);
    } else {
      setTitle('');
      setValue(0);
    }
  }, [initialData]);

  const isFormValid = title.trim() !== '' && value > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    onSubmit({
      title,
      value,
      month,
      year,
    });

    if (!isEditing) {
      setTitle('');
      setValue(0);
    }
  };

  return (
    <form
      data-slot="income-form"
      onSubmit={handleSubmit}
      className="flex flex-col gap-4"
      {...props}
    >
      <div className="grid grid-cols-2 gap-4">
        <FormField>
          <Label htmlFor="title">
            Título <span className="text-destructive">*</span>
          </Label>
          <Input
            id="title"
            value={title}
            placeholder="Ex: Salário"
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </FormField>
        <FormField>
          <Label htmlFor="value">
            Valor <span className="text-destructive">*</span>
          </Label>
          <CurrencyInput
            id="value"
            value={value}
            onChange={setValue}
            required
          />
        </FormField>
      </div>

      <div className="flex gap-2">
        {onClose && (
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={onClose}
          >
            Cancelar
          </Button>
        )}
        <Button type="submit" className="flex-1" disabled={!isFormValid}>
          {isEditing ? 'Salvar alterações' : 'Adicionar renda'}
        </Button>
      </div>
    </form>
  );
}
